import type { Answers } from "../../../../features/questionnaire";
import type { EvaluatedAnswer, VisibilityMap } from "./types";

export type ExpressionContext = {
  answers: Answers;
  visibilityMap?: VisibilityMap;
  evaluatedAnswers?: EvaluatedAnswer[];
};

/**
 * Evaluates template literal expressions with embedded question references and functions
 *
 * Supported syntax:
 * - Simple variable substitution: "${questionName}"
 * - Function calls: "sum(${q1}, ${q2}, ${q3})"
 * - Mixed expressions: "${income} * 12 + ${bonus}"
 *
 * @param expression The template literal expression to evaluate
 * @param context The context containing answers and visibility map
 * @returns The evaluated result as string, number, boolean, or null
 */
export const evaluateExpression = (
  expression: string,
  context: ExpressionContext,
): string | number | boolean | null => {
  if (!expression) {
    return null;
  }

  try {
    // Create a safe evaluation context with helper functions
    const evaluationContext = createEvaluationContext(context);

    // Replace ${questionName} with actual values
    const processedExpression = replaceQuestionReferences(expression, context);

    // Evaluate the processed expression
    return evaluateProcessedExpression(processedExpression, evaluationContext);
  } catch (error) {
    console.warn("Expression evaluation failed:", expression, error);
    return null;
  }
};

/**
 * Replace ${questionName} references with actual values from answers
 */
const replaceQuestionReferences = (
  expression: string,
  context: ExpressionContext,
): string => {
  return expression.replace(
    /\$\{([^}]+)\}/g,
    (_match, questionName: string) => {
      const trimmedName = questionName.trim();
      const answer =
        context.answers[trimmedName] ??
        context.evaluatedAnswers?.find(ea => ea.question === trimmedName)
          ?.value;

      if (answer === undefined || answer === null) {
        return "null";
      }

      if (answer === "") {
        return '""';
      }

      // Convert answer to appropriate format for evaluation
      if (typeof answer === "string") {
        // Check if string represents a number
        const numValue = Number(answer);
        if (!isNaN(numValue) && isFinite(numValue)) {
          return String(numValue);
        }
        return `"${answer.replace(/"/g, '\\"')}"`;
      }

      if (Array.isArray(answer)) {
        return `[${answer
          .map(item => {
            const numValue = Number(item);
            if (!isNaN(numValue) && isFinite(numValue)) {
              return String(numValue);
            }
            return `"${String(item).replace(/"/g, '\\"')}"`;
          })
          .join(", ")}]`;
      }

      return String(answer);
    },
  );
};

/**
 * Create evaluation context with helper functions
 */
const createEvaluationContext = (_context: ExpressionContext) => {
  return {
    // Mathematical functions
    sum: (...values: (number | string | (number | string)[])[]) => {
      const numbers = flattenAndConvertToNumbers(values);
      return numbers.reduce((acc, val) => acc + val, 0);
    },

    max: (...values: (number | string | (number | string)[])[]) => {
      const numbers = flattenAndConvertToNumbers(values);
      return numbers.length > 0 ? Math.max(...numbers) : 0;
    },

    min: (...values: (number | string | (number | string)[])[]) => {
      const numbers = flattenAndConvertToNumbers(values);
      return numbers.length > 0 ? Math.min(...numbers) : 0;
    },

    avg: (...values: (number | string | (number | string)[])[]) => {
      const numbers = flattenAndConvertToNumbers(values);
      return numbers.length > 0
        ? numbers.reduce((acc, val) => acc + val, 0) / numbers.length
        : 0;
    },

    count: (value: unknown) => {
      if (Array.isArray(value)) {
        return value.length;
      }
      return value !== null && value !== undefined ? 1 : 0;
    },

    // String functions
    concat: (...values: unknown[]) => {
      return values
        .map(v => {
          if (v === null || v === undefined) return "";
          if (
            typeof v === "string" ||
            typeof v === "number" ||
            typeof v === "boolean"
          ) {
            return String(v);
          }
          return "[object Object]";
        })
        .join("");
    },

    length: (value: unknown) => {
      if (typeof value === "string" || Array.isArray(value)) {
        return value.length;
      }
      return 0;
    },

    // Conditional functions
    coalesce: (...values: unknown[]) => {
      for (const value of values) {
        if (value !== null && value !== undefined && value !== "") {
          return value;
        }
      }
      return null;
    },

    // Utility functions
    Math,
    String,
    Number,
    Boolean,
  };
};

/**
 * Flatten nested arrays and convert all values to numbers
 */
const flattenAndConvertToNumbers = (
  values: (number | string | (number | string)[])[],
): number[] => {
  const flattened: (number | string)[] = [];

  for (const value of values) {
    if (Array.isArray(value)) {
      flattened.push(...value);
    } else {
      flattened.push(value);
    }
  }

  return flattened.map(val => {
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  });
};

/**
 * Safely evaluate the processed expression using Function constructor
 * This is safer than eval() as we control the context
 */
const evaluateProcessedExpression = (
  expression: string,
  evaluationContext: Record<string, unknown>,
): string | number | boolean | null => {
  // Extract function names and values for the evaluation context
  const contextKeys = Object.keys(evaluationContext);
  const contextValues = Object.values(evaluationContext);

  // Create a function that evaluates the expression in the controlled context
  // eslint-disable-next-line @typescript-eslint/no-implied-eval
  const evaluatorFunction = new Function(
    ...contextKeys,
    `"use strict"; return (${expression});`,
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const result = evaluatorFunction(...contextValues);

  // Ensure we return the expected types
  if (
    typeof result === "string" ||
    typeof result === "number" ||
    typeof result === "boolean"
  ) {
    return result;
  }

  return result === null || result === undefined ? null : String(result);
};
